import cx from "classnames";
import React, { type FunctionComponent } from "react";

import { type CachedCell } from "~/models/cell";

import { type CellInfo } from "../../models/cell";
import { useQueryBuilderStateManager } from "../canvas/editor/cells/QueryBuilderStateProvider";
import { MarkupValue } from "../inspector/values/MarkupValue";
import { isExpandable } from "./traverseValue/TypeTagInfo";
import { useValueInspector } from "./useValueInspector";
import { InspectorRow } from "./value-display/InspectorRow";
import { InspectorRowValue } from "./value-display/InspectorRowValue";
import { classDoNotCollapseQueryBuilderOnClick } from "./value-display/hasClassDoNotCollapseQueryBuilderOnClick";
import { useInspectorKeyNavigation } from "./value-display/useInspectorKeyNavigation";

const ValueInspector: FunctionComponent<{
	cellID: string;
	cellName: string;
	value: unknown;
}> = ({ cellID, cellName, value }) => {
	const [fieldExpander, rows] = useValueInspector(cellName, cellID, value);

	const { queryBuilderState, queryBuilderStateManager } = useQueryBuilderStateManager();

	const [makeButtonRef, makeNavigator] = useInspectorKeyNavigation(rows);

	if (value instanceof Element || value instanceof Text) {
		return (
			<div className="p-2">
				<MarkupValue value={value} />
			</div>
		);
	}

	return (
		<div
			className={cx(
				classDoNotCollapseQueryBuilderOnClick,
				"text-syntax-normal flex flex-col space-y-1 overflow-x-auto whitespace-nowrap p-2 font-mono text-sm font-normal"
			)}
		>
			{rows.map((row, i) => {
				const lastComponent = row.path[row.path.length - 1];
				const showExpandButton = lastComponent
					? isExpandable(lastComponent.typeInfo) && row.type !== "ExpandedValueClosingRow"
					: false;
				return (
					<InspectorRow
						key={i}
						ref={makeButtonRef(i)}
						navigator={makeNavigator(i)}
						prefix={row.path}
						fieldExpander={fieldExpander}
						showExpandButton={showExpandButton}
					>
						<InspectorRowValue
							cellID={cellID}
							row={row}
							queryBuilderState={queryBuilderState}
							queryBuilderStateManager={queryBuilderStateManager}
						/>
					</InspectorRow>
				);
			})}
		</div>
	);
};

/**
 * API-compatible replacement for `inspector/Inspector.tsx`. It is not yet feature complete.
 */
export const Inspector: FunctionComponent<{
	cellInfo: CellInfo;
	cachedCell: CachedCell;
}> = ({ cellInfo, cachedCell }) => {
	if (cachedCell.result === undefined) {
		return null;
	}

	if (cachedCell.result.type !== "FULFILLED") {
		return null;
	}

	const value = cachedCell.result.value;
	return (
		<ValueInspector
			cellName={cellInfo.cell.cellName ?? ""}
			cellID={cellInfo.cell.id}
			value={value}
		/>
	);
};
